const truthy = (value) => String(value || "").trim().toLowerCase() === "true";
const allowedStates = new Set(["review", "approved", "live"]);
// HALO_PRICING_VERSION pins the 2026-09-26 App Store Connect read-back.
// That read-back is not the website list and is not a live exchange rate.
export const HALO_PRICING_VERSION = "2026-09-26.2";
const HALO_TRIAL_DAYS = 7;
// Source-owned launch gates. Environment state alone must never publish a
// price, trial claim, or Store CTA. HALO_PRICING_RELEASE_APPROVED is the
// flick-switch for website list prices. It stays false until a later release.
export const HALO_PRICING_RELEASE_APPROVED = false;
export const HALO_STORE_RELEASE_APPROVALS = Object.freeze({
  microsoft: false,
  apple_ios: false,
  apple_macos: false,
  google_play: false,
});
// Website marketing list is USD only. Local currency is Stripe Adaptive Pricing
// inside Checkout, not a second list price. Do not promise renewal FX.
export const HALO_WEBSITE_LIST_PRICE = Object.freeze({
  currency: "USD",
  monthly: "US$9.99",
  monthlyCents: 999,
  annual: "US$79.99",
  annualCents: 7999,
});

const STORE_HOSTS = {
  microsoft: new Set(["apps.microsoft.com", "www.microsoft.com"]),
  apple_ios: new Set(["apps.apple.com"]),
  apple_macos: new Set(["apps.apple.com"]),
  google_play: new Set(["play.google.com"]),
};

const normalizeState = (value) => {
  const state = String(value || "review").trim().toLowerCase();
  return allowedStates.has(state) ? state : "review";
};

function safeStoreUrl(id, value) {
  if (!value) return "";
  try {
    const url = new URL(String(value).trim());
    if (
      url.protocol !== "https:"
      || !STORE_HOSTS[id]?.has(url.hostname)
      || url.username
      || url.password
      || url.hash
    ) {
      return "";
    }
    return url.toString();
  } catch {
    return "";
  }
}

const store = ({ id, name, platform, state, url, releaseApproved }) => {
  const safeUrl = safeStoreUrl(id, url);
  const normalizedState = normalizeState(state);
  const releaseState = releaseApproved ? normalizedState : "review";
  return {
    id,
    name,
    platform,
    state: releaseState === "live" && !safeUrl ? "review" : releaseState,
    url: safeUrl,
  };
};

export function buildHaloStorefront(env = process.env) {
  const pricingPublished =
    HALO_PRICING_RELEASE_APPROVED
    && truthy(env.REACT_APP_HALO_PRICING_PUBLISHED)
    && String(env.REACT_APP_HALO_PRICING_VERSION || "").trim() === HALO_PRICING_VERSION;
  const listPrice = HALO_WEBSITE_LIST_PRICE;

  return {
    product_name: "Halo SuperVPN",
    microsoft_fallback_name: "Halo SuperVPN by LightningFlow",
    identifiers: {
      ios: "ai.lightningflow.halo.ios",
      macos: "ai.lightningflow.halo.macos",
      android: "com.lightningflow.halo",
    },
    pricing_published: pricingPublished,
    default_currency: pricingPublished ? listPrice.currency : "",
    monthly_price: pricingPublished ? listPrice.monthly : "",
    annual_price: pricingPublished ? listPrice.annual : "",
    monthly_cents: pricingPublished ? listPrice.monthlyCents : null,
    annual_cents: pricingPublished ? listPrice.annualCents : null,
    // Marketing pages do not publish AUD, EUR, GBP or other regional list prices.
    regional_prices: [],
    trial_days: pricingPublished ? HALO_TRIAL_DAYS : 0,
    device_limit: 3,
    stores: [
      store({
        id: "microsoft",
        name: "Microsoft Store",
        platform: "Windows",
        state: env.REACT_APP_HALO_MICROSOFT_STORE_STATE,
        url: env.REACT_APP_HALO_MICROSOFT_STORE_URL,
        releaseApproved: HALO_STORE_RELEASE_APPROVALS.microsoft,
      }),
      store({
        id: "apple_ios",
        name: "Apple App Store",
        platform: "iPhone and iPad",
        state: env.REACT_APP_HALO_IOS_STORE_STATE,
        url: env.REACT_APP_HALO_IOS_STORE_URL,
        releaseApproved: HALO_STORE_RELEASE_APPROVALS.apple_ios,
      }),
      store({
        id: "apple_macos",
        name: "Mac App Store",
        platform: "Mac",
        state: env.REACT_APP_HALO_MAC_STORE_STATE,
        url: env.REACT_APP_HALO_MAC_STORE_URL,
        releaseApproved: HALO_STORE_RELEASE_APPROVALS.apple_macos,
      }),
      store({
        id: "google_play",
        name: "Google Play",
        platform: "Android",
        state: env.REACT_APP_HALO_GOOGLE_PLAY_STATE,
        url: env.REACT_APP_HALO_GOOGLE_PLAY_URL,
        releaseApproved: HALO_STORE_RELEASE_APPROVALS.google_play,
      }),
    ],
  };
}

export const haloStorefront = buildHaloStorefront();

export function isStoreLive(value) {
  return value?.state === "live" && Boolean(safeStoreUrl(value.id, value.url));
}

export function storeStatusLabel(value) {
  if (isStoreLive(value)) return `Get Halo SuperVPN for ${value.platform}`;
  return `Halo SuperVPN for ${value?.platform || "your device"}`;
}

export const allHaloStoresLive = haloStorefront.stores.every(isStoreLive);
